<template>
  <div class="map-container" :class="{ 'hide-draw': hideControl }">
    <div class="control-box">
      <div style="display: flex">
        <select v-model="searchType" class="form-control" style="width: 180px; border-radius: 0px; margin-right: 8px">
          <option value="postal">Postal code</option>
          <option value="ods">ODS code</option>
        </select>
        <input
          v-model="searchQuery"
          type="text"
          class="form-control portal-code-search"
          :placeholder="searchType === 'postal' ? 'Search Postal Code' : 'Search ODS Code'"
        />
        <div class="search-btn" @click="search">&#x1F50D;</div>
        <!-- Unicode for search icon -->
      </div>
      <div class="draw-btn" :class="{ disabled: polygonDrawn }" @click="startDraw">Draw</div>
      <div class="clear-btn" @click="clearAndStylePolygon">Clear</div>
    </div>
    <div id="map"></div>
  </div>
</template>

<script>
import maplibregl from "maplibre-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "maplibre-gl/dist/maplibre-gl.css";
import axios from "axios"; // Assuming axios for HTTP requests

export default {
  name: "MapComponent",
  props: {
    location: String,
    title: String,
    item_key: {
      type: String,
      required: true,
    },
    item_value: {
      type: Array,
      required: false,
    },
    updateValue: {
      type: Function,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    selectedItem: {
      type: Object,
      required: false,
    },
    resolveOdsToCoords: {
      type: Function,
      required: false,
    },
    fhirBase: {
      type: String,
      default: "https://directory.spineservices.nhs.uk/STU3",
    },
    ckanBase: {
      type: String,
      default: "https://opendata.england.nhs.uk",
    },
    ckanResourceId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      map: null,
      draw: null,
      polygonDrawn: false,
      drawingActive: false,
      boundries: [],
      hideControl: false,
      searchType: "postal",
      searchQuery: "",
    };
  },
  watch: {
    "selectedItem.boundaries"(newBoundaries) {
      if (newBoundaries && newBoundaries.length) {
        this.loadBoundaries(newBoundaries);
      }
    },
  },
  mounted() {
    this.initializeMap();
  },
  methods: {
    bringDrawCtrlToFront() {
      try {
        const container = this.map.getContainer();
        const topRight = container.querySelector(
          ".maplibregl-ctrl-top-right"
        );
        const drawCtrl = container.querySelector(
          ".mapboxgl-ctrl-group.mapboxgl-ctrl"
        );
        if (topRight && drawCtrl) topRight.appendChild(drawCtrl);
      } catch (e) {
        if (e) return;
      }
    },
    search() {
      if (!this.searchQuery) return;
      if (this.searchType === "postal") {
        // Using postcodes.io to get the latitude and longitude for a UK postcode
        axios
          .get(
            `https://api.postcodes.io/postcodes/${encodeURIComponent(
              this.searchQuery
            )}`
          )
          .then((response) => {
            const { latitude, longitude } = response.data.result;
            this.map.flyTo({
              center: [longitude, latitude],
              essential: true,
              zoom: 16,
            });
          })
          .catch((error) => {
            console.error("Error fetching postcode data: ", error);
          });
      } else if (this.searchType === "ods") {
        if (typeof this.resolveOdsToCoords === "function") {
          Promise.resolve(this.resolveOdsToCoords(this.searchQuery))
            .then((res) => {
              if (!res) return;
              const { latitude, longitude } = res;
              if (
                typeof latitude === "number" &&
                typeof longitude === "number"
              ) {
                this.map.flyTo({
                  center: [longitude, latitude],
                  essential: true,
                  zoom: 16,
                });
              }
            })
            .catch((err) => {
              console.error("Error resolving ODS code:", err);
            });
        } else if (this.fhirBase) {
          this.resolveOdsViaFHIR(this.searchQuery)
            .then((res) => {
              if (!res) return;
              const { latitude, longitude } = res;
              if (
                typeof latitude === "number" &&
                typeof longitude === "number"
              ) {
                this.map.flyTo({
                  center: [longitude, latitude],
                  essential: true,
                  zoom: 16,
                });
              }
            })
            .catch((err) => {
              console.error("FHIR ODS lookup failed:", err);
            });
        } else if (this.ckanResourceId) {
          this.resolveOdsViaCKAN(this.searchQuery)
            .then((res) => {
              if (!res) return;
              const { latitude, longitude } = res;
              if (
                typeof latitude === "number" &&
                typeof longitude === "number"
              ) {
                this.map.flyTo({
                  center: [longitude, latitude],
                  essential: true,
                  zoom: 16,
                });
              }
            })
            .catch((err) => {
              console.error("CKAN ODS lookup failed:", err);
            });
        } else {
          console.warn(
            "No resolveOdsToCoords prop provided; cannot search ODS code."
          );
        }
      }
    },
    async resolveOdsViaCKAN(ods) {
      const url = `${this.ckanBase}/api/3/action/datastore_search`;
      const { data } = await axios.get(url, {
        params: { resource_id: this.ckanResourceId, q: ods, limit: 1 },
      });
      const recs = data && data.result && data.result.records;
      const record = Array.isArray(recs) && recs.length ? recs[0] : null;
      if (!record) return null;
      const postcode =
        record.POSTCODE ||
        record.Postcode ||
        record.postcode ||
        record.PCDS ||
        null;
      if (!postcode) return null;
      const pcRes = await axios.get(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
      );
      const { latitude, longitude } = pcRes.data.result;
      return { latitude, longitude };
    },
    async resolveOdsViaFHIR(ods) {
      const url = `${this.fhirBase}/Organization/${encodeURIComponent(ods)}`;
      const { data } = await axios.get(url, {
        headers: { Accept: "application/fhir+json" },
      });
      const org = data || {};
      let addr = null;
      if (Array.isArray(org.address)) {
        addr = org.address.find((a) => a && (a.postalCode || a.postalcode)) || org.address[0] || null;
      } else if (org.address && typeof org.address === "object") {
        addr = org.address;
      }
      const rawPostcode = addr && (addr.postalCode || addr.postalcode);
      if (!rawPostcode) return null;
      const postcode = String(rawPostcode).trim().toUpperCase();
      const pcRes = await axios.get(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
      );
      const { latitude, longitude } = pcRes.data.result;
      return { latitude, longitude };
    },
    handleControl() {
      this.hideControl = this.selectedItem?.boundaries?.length ? true : false;
    },
    clearAndStylePolygon() {
      // Clear the drawn polygon using Mapbox Draw controls
      this.draw.deleteAll();
      this.polygonDrawn = false;

      // Check if a boundary source exists and remove its data
      if (this.map.getSource("boundary")) {
        this.map.getSource("boundary").setData({
          type: "FeatureCollection",
          features: [], // No features means no polygon
        });
      }

      // Optionally, clear selected item boundaries data
      if (this.selectedItem && this.selectedItem.boundaries) {
        // Assuming selectedItem.boundaries is reactive, reset it
        this.selectedItem.boundaries = [];
      }

      // Emit an update to the parent component if needed
      this.updateValue(this.item_key, []);
      this.handleControl();
    },

    initializeMap() {
      this.map = new maplibregl.Map({
        container: "map",
        style: {
          version: 8,
          sources: {
            "osm-tiles": {
              type: "raster",
              tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
              tileSize: 256,
              attribution: "",
            },
          },
          layers: [
            {
              id: "osm-tiles",
              type: "raster",
              source: "osm-tiles",
            },
          ],
        },
        center: [-2.305039, 54.131467],
        zoom: 4,
      });

      this.map.addControl(new maplibregl.NavigationControl(), "top-left");

      this.map.on("load", () => {
        this.setupDrawingTools();
        if (this.selectedItem && this.selectedItem.boundaries) {
          this.loadBoundaries(this.selectedItem.boundaries);
        }
        this.handleControl();
        this.$nextTick(() => this.bringDrawCtrlToFront());
      });

      this.map.on("draw.create", this.handleDraw);
      this.map.on("draw.delete", this.handleDraw);
      this.map.on("draw.update", this.handleDraw);
    },
    loadBoundaries(boundaries) {
      const feature = {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [boundaries.map((coord) => [coord.lng, coord.lat])],
        },
      };

      // Check if the source and layer already exist
      if (this.map.getSource("boundary")) {
        // Update the existing source with new data
        this.map.getSource("boundary").setData({
          type: "FeatureCollection",
          features: [feature],
        });
      } else {
        // Add new source and layer if they don't exist
        this.map.addSource("boundary", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [feature],
          },
        });

        this.map.addLayer({
          id: "boundary",
          type: "fill",
          source: "boundary",
          layout: {},
          paint: {
            "fill-color": "blue", // Customize fill color
            "fill-opacity": 0.4, // Customize fill opacity
          },
        });
        // Line layer for polygon outline
        this.map.addLayer({
          id: "boundary-stroke",
          type: "line",
          source: "boundary",
          layout: {},
          paint: {
            "line-color": "black", // Customize stroke color
            "line-width": 3, // Customize stroke width
          },
        });
      }

      // Fit the map to the polygon bounds
      const bounds = new maplibregl.LngLatBounds();
      feature.geometry.coordinates[0].forEach((coord) => bounds.extend(coord));
      this.map.fitBounds(bounds, {
        padding: 20,
      });
      this.polygonDrawn = true;
    },
    setupDrawingTools() {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        styles: [
          // Custom style for polygon fill
          {
            id: "gl-draw-polygon-fill",
            type: "fill",
            filter: ["all", ["==", "$type", "Polygon"]],
            paint: {
              "fill-color": "#005fba", // Custom fill color
              "fill-opacity": 0.5, // Custom opacity
            },
          },

          // Custom style for polygon stroke
          {
            id: "gl-draw-polygon-stroke",
            type: "line",
            filter: ["all", ["==", "$type", "Polygon"]],
            paint: {
              "line-color": "#005fba", // Custom stroke color
              "line-width": 3, // Custom stroke width
            },
          },

          // Include other styles for active states and other shape types
          {
            id: "gl-draw-polygon-and-line-vertex-active",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "circle-radius": 5,
              "circle-color": "#FFA500",
            },
          },
        ],
      });
      this.map.addControl(this.draw, "top-right");
      this.$nextTick(() => this.bringDrawCtrlToFront());
    },
    startDraw() {
      if (!this.draw || this.polygonDrawn) return;
      try {
        this.draw.changeMode("draw_polygon");
      } catch (e) {
        if (e) return;
      }
    },
    handleDraw() {
      const data = this.draw.getAll();
      console.log(data);
      this.polygonDrawn = data.features.length > 0;
      if (
        data?.features &&
        data?.features.length &&
        data?.features[0].geometry?.coordinates
      ) {
        this.convertAndSetBoundaries(data.features[0].geometry.coordinates);
      }
    },
    toggleDrawing() {
      this.drawingActive = !this.drawingActive;
      if (this.drawingActive) {
        this.map.addControl(this.draw);
      } else {
        this.map.removeControl(this.draw);
      }
    },
    convertAndSetBoundaries(inputCoords) {
      console.log("coordinates", JSON.stringify(inputCoords));
      if (Array.isArray(inputCoords) && inputCoords.length) {
        const coordinates = inputCoords[0].map((coord) => ({
          lng: coord[0],
          lat: coord[1],
        }));
        console.log("converted corrdinates", coordinates);
        this.updateValue(this.item_key, coordinates);
        this.handleControl();
      }
    },
    boundriesToMapBoundry(inputData) {
      return [inputData.map((coord) => [coord.lng, coord.lat])];
    },
  },
};
</script>

<style>
.map-container {
  position: relative;
  margin-top: 20px;
  width: 100%;
  height: 580px;
}

#map {
  width: 100%;
  height: 500px;
  position: relative;
}

.control-box {
  top: 10px;
  right: 10px;
  display: flex;
  padding: 15px 0px;
  text-align: center;
  z-index: 1; /* Ensures the button box is above the map */
  cursor: pointer;
  color: #007cff;
  text-align: right;
  justify-content: space-between;
}

.maplibregl-control-container {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1000;
}

.canvas.maplibregl-canvas {
  position: relative;
}

.mapboxgl-ctrl-group.mapboxgl-ctrl {
  right: 0;
  position: absolute;
  top: 0;
}

.maplibregl-ctrl-bottom-right {
  display: none;
}
.hide-draw .mapboxgl-ctrl-group.mapboxgl-ctrl {
  display: none;
}
.portal-code-search {
  width: 300px;
  border-radius: 0px;
}
.control-box {
  display: flex;
  align-items: center;
  gap: 10px; /* Space between elements */
}

.search-btn,
.clear-btn,
.draw-btn {
  padding: 8px 12px; /* Padding for better touch area */
  background-color: #e2e2e2; /* Gray background */
  color: black; /* Color for the text and icon */
  font-size: 16px; /* Adjust size as needed */
  cursor: pointer; /* Cursor to pointer to indicate it's clickable */
  border: 1px solid #cccccc; /* Remove any default border */
  border-radius: 0px; /* Optional: rounded corners */
  height: 39px;
}

.search-btn:hover,
.clear-btn:hover,
.draw-btn:hover {
  background-color: #bbb; /* Darker shade on hover */
}

.portal-code-search {
  flex-grow: 1; /* Allows the input to take up remaining space */
  padding: 8px;
  border: 1px solid #ccc;
}
/* Ensure all control groups are on top */
.mapboxgl-ctrl-group.mapboxgl-ctrl,
.maplibregl-ctrl.maplibregl-ctrl-group {
  z-index: 1001;
}
</style>
